import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountPhoneNumberView } from '../../add-account-phone-numbers/models/account-phone-number-view.model';
import { PhoneNumberInfoService } from '../services/phone-number-info.service';

@Component({
    providers: [PhoneNumberInfoService],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-account-phone-numbers',
    styleUrls: ['./account-phone-numbers.component.scss'],
    templateUrl: './account-phone-numbers.component.html',
    standalone: false
})
export class AccountPhoneNumbersComponent implements ControlValueAccessor, OnDestroy {
   phoneNumberItems: AccountPhoneNumberView[];
   disabled = false;
   selectedPhoneNumber: string | null;
   private destroyed$ = new Subject<void>();
   onTouched: () => void;
   private onChange: (value: Array<AccountPhoneNumberView> | null) => void;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private phoneNumberInfoService: PhoneNumberInfoService
   ) {
      control.valueAccessor = this;
      this.phoneNumberInfoService.outsidePointerEvent$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         setTimeout(() => {
            this.selectedPhoneNumber = null;
            this.changeDetectorRef.markForCheck();
         }, 0);
      });
   }

   writeValue(phoneNumberItems: AccountPhoneNumberView[] | null): void {
      if (phoneNumberItems) {
         this.phoneNumberItems = phoneNumberItems.sort((pn1, pn2) => {
            if (!pn1.phoneNumber && !pn2.phoneNumber) {
               return 0;
            }

            if (!pn1.phoneNumber || pn1.phoneNumber > (pn2.phoneNumber ?? '')) {
               return 1;
            }

            if (!pn2.phoneNumber || pn1.phoneNumber < pn2.phoneNumber) {
               return -1;
            }

            return 0;
         });
      }
      this.changeDetectorRef.markForCheck();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   registerOnChange(onChange: (value: AccountPhoneNumberView[] | null) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState(disabled: boolean): void {
      this.disabled = disabled;
      this.changeDetectorRef.markForCheck();
   }

   onDetailsClick(phoneNumber: AccountPhoneNumberView, button: HTMLElement): void {
      if (!this.selectedPhoneNumber) {
         this.phoneNumberInfoService.showPhoneNumberInfo(phoneNumber, button, this.changeDetectorRef);
         setTimeout(() => {
            this.selectedPhoneNumber = phoneNumber.phoneNumber ?? null;
            this.changeDetectorRef.markForCheck();
         }, 0);
      }
   }

   onRemoveClick(phoneNumber: AccountPhoneNumberView) {
      const index = this.phoneNumberItems.findIndex((pn) => {
         return pn.phoneNumber === phoneNumber.phoneNumber;
      });

      if (index > -1) {
         this.phoneNumberItems.splice(index, 1);
      }

      this.onChange(this.phoneNumberItems);
      this.changeDetectorRef.detectChanges();
   }
}
